"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function HoaPrograms() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const tiers = [{
    index: "01 / ESSENTIAL",
    name: "Essential Care",
    coords: "N40°44'30\" W74°00'21\"",
    description: "Our foundational arboriculture package. Includes routine health assessments, structural pruning, and seasonal soil enrichment to ensure your community's canopy remains vibrant and safe year-round.",
    inclusions: ["Annual health assessment", "Structural pruning cycles", "Seasonal soil enrichment", "24/7 emergency response"],
    recommended: false
  }, {
    index: "02 / PREMIER",
    name: "Premier Canopy",
    coords: "N35°00'42\" E135°46'05\"",
    description: "The signature management tier. Comprehensive tree inventory mapping, advanced pest management, and proactive risk mitigation strategies tailored for high-value community landscapes.",
    inclusions: ["Digital inventory mapping", "Advanced pest management", "Proactive risk mitigation", "Dedicated account arborist"],
    recommended: true
  }, {
    index: "03 / LEGACY",
    name: "Legacy Preservation",
    coords: "S77°50'47\" E166°40'06\"",
    description: "Unrepeatable care for historic and heritage specimens. Specialized root zone restoration, lightning protection systems, and long-term growth planning for the community's most iconic trees.",
    inclusions: ["Heritage tree restoration", "Lightning protection systems", "Long-term growth planning", "Bespoke soil diagnostics"],
    recommended: false
  }];
  return <section data-section-id="2043" id="hoa-programs" className="bg-background text-foreground py-28 md:py-36 font-default">
      <div className="max-w-[1480px] mx-auto px-8 md:px-12 lg:px-16">
        {}
        <div ref={header.ref} className={`relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div aria-hidden="true" className="pointer-events-none absolute -top-8 left-0 lg:-top-16 lg:-left-4 font-default font-black select-none leading-none" style={{
          fontSize: "clamp(8rem, 22vw, 22rem)",
          color: "transparent",
          WebkitTextStroke: "1px var(--foreground)",
          opacity: 0.08,
          letterSpacing: "-0.05em"
        }}>
            01
          </div>

          <div className="lg:col-span-8 relative z-10">
            {}
            <div className="flex items-center gap-3 mb-8">
              <span className="font-mono uppercase" style={{
              fontSize: "clamp(0.6875rem, 0.8vw, 0.8125rem)",
              letterSpacing: "0.18em",
              color: "var(--secondary)"
            }}>
                § 01 — HOA Programs / Three Tiers
              </span>
            </div>

            {}
            <div className="flex items-center gap-0 mb-8">
              <div className="h-px flex-1 bg-[color:var(--secondary)] opacity-40" />
              <div className="h-1.5 w-1.5 bg-[color:var(--primary)]" />
            </div>

            <h2 className="font-default font-black" style={{
            fontSize: "clamp(2.5rem, 5.5vw, 5.25rem)",
            letterSpacing: "-0.035em",
            lineHeight: 0.92
          }}>
              Three levels of{" "}
              <span className="font-serif italic font-normal" style={{
              letterSpacing: "-0.02em"
            }}>
                stewardship
              </span>
              .
            </h2>
          </div>

          <div className="lg:col-span-4 lg:pt-4 relative z-10 flex flex-col justify-between">
            <p className="font-serif" style={{
            fontSize: "clamp(1.0625rem, 1.25vw, 1.25rem)",
            lineHeight: 1.55,
            color: "var(--secondary)"
          }}>
              Every community landscape is managed by a certified arborist.
              Select your service level.
            </p>

            <div className="mt-8">
              <Link to="/OurExpertisePage" className="group inline-flex items-center gap-2 font-mono uppercase underline underline-offset-[6px] decoration-1" style={{
              fontSize: "0.75rem",
              letterSpacing: "0.16em",
              color: "var(--foreground)"
            }}>
                <span className="transition-colors duration-200 group-hover:text-[color:var(--primary)] group-hover:decoration-[color:var(--primary)]">
                  Compare Programs
                </span>
                <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" strokeWidth={1.25} />
              </Link>
            </div>
          </div>
        </div>

        {}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {tiers.map((tier, i) => <TierCard key={i} tier={tier} index={i} />)}
        </div>
      </div>
    </section>;
}
function TierCard({
  tier,
  index
}: {
  tier: {
    index: string;
    name: string;
    coords: string;
    description: string;
    inclusions: string[];
    recommended: boolean;
  };
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <div ref={ref} data-index={index} className="group relative bg-[color:var(--card)] p-10 transition-all duration-300 ease-out" style={{
    border: "1px solid var(--secondary)",
    boxShadow: "0 24px 48px -16px hsl(0 0% 0% / 0.18)",
    transitionDelay: `${index * 80}ms`,
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? "translateY(0)" : "translateY(24px)"
  }} onMouseEnter={e => {
    e.currentTarget.style.border = "1px solid var(--primary)";
    e.currentTarget.style.boxShadow = "0 32px 60px -16px hsl(0 0% 0% / 0.28)";
  }} onMouseLeave={e => {
    e.currentTarget.style.border = "1px solid var(--secondary)";
    e.currentTarget.style.boxShadow = "0 24px 48px -16px hsl(0 0% 0% / 0.18)";
  }}>
      {}
      <CornerTick position="tl" />
      <CornerTick position="tr" />
      <CornerTick position="bl" />
      <CornerTick position="br" />

      {}
      <div className="flex items-center justify-between mb-6">
        <span className="font-mono uppercase" style={{
        fontSize: "0.75rem",
        letterSpacing: "0.16em",
        color: "var(--secondary)"
      }}>
          {tier.index}
        </span>
        {tier.recommended && <div className="flex items-center gap-2">
            <span className="font-mono uppercase" style={{
          fontSize: "0.6875rem",
          letterSpacing: "0.18em",
          color: "var(--foreground)"
        }}>
              Recommended
            </span>
            <div className="h-1.5 w-1.5 bg-[color:var(--primary)]" />
          </div>}
      </div>

      {}
      <h3 className="font-default font-bold mb-2" style={{
      fontSize: "clamp(1.5rem, 2vw, 2rem)",
      letterSpacing: "-0.02em",
      lineHeight: 1.05
    }}>
        {tier.name}
      </h3>

      <div className="font-mono mb-6" style={{
      fontSize: "0.75rem",
      letterSpacing: "0.08em",
      color: "var(--secondary)"
    }}>
        {tier.coords}
      </div>

      {}
      <div className="flex items-center gap-0 mb-6">
        <div className="h-px flex-1 bg-[color:var(--secondary)] opacity-40" />
        <div className="h-1.5 w-1.5 bg-[color:var(--primary)]" />
      </div>

      {}
      <p className="font-serif mb-8" style={{
      fontSize: "1rem",
      lineHeight: 1.65,
      color: "var(--secondary)"
    }}>
        {tier.description}
      </p>

      {}
      <ul className="space-y-3 mb-10">
        {tier.inclusions.map((inc, j) => <li data-index={j} key={j} className="flex items-start gap-3 font-mono uppercase" style={{
        fontSize: "0.6875rem",
        letterSpacing: "0.14em",
        lineHeight: 1.4,
        color: "var(--foreground)"
      }}>
            <span className="mt-1 h-1 w-1 bg-[color:var(--primary)] shrink-0" aria-hidden="true" />
            <span>{inc}</span>
          </li>)}
      </ul>

      {}
      <Button asChild variant="link" className="p-0 h-auto font-mono uppercase no-underline hover:no-underline">
        <Link to="/GetQuotePage" className="group/cta inline-flex items-center gap-2 underline underline-offset-[6px] decoration-1" style={{
        fontSize: "0.75rem",
        letterSpacing: "0.16em",
        color: "var(--foreground)"
      }}>
          <span className="transition-colors duration-200 group-hover/cta:text-[color:var(--primary)] group-hover/cta:decoration-[color:var(--primary)]">
            Request Proposal
          </span>
          <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/cta:translate-x-1" strokeWidth={1.25} />
        </Link>
      </Button>
    </div>;
}
function CornerTick({
  position
}: {
  position: "tl" | "tr" | "bl" | "br";
}) {
  const base = "absolute pointer-events-none transition-all duration-300";
  const size = "w-2 h-2 group-hover:w-3 group-hover:h-3";
  const offsets: Record<string, string> = {
    tl: "top-0 left-0",
    tr: "top-0 right-0",
    bl: "bottom-0 left-0",
    br: "bottom-0 right-0"
  };
  const borders: Record<string, string> = {
    tl: "border-t border-l",
    tr: "border-t border-r",
    bl: "border-b border-l",
    br: "border-b border-r"
  };
  return <span aria-hidden="true" className={`${base} ${size} ${offsets[position]} ${borders[position]} border-[color:var(--foreground)]`} />;
}